// File and image helper functions.

import { MIME_EXTENSIONS, SUPPORTED_IMAGE_FORMATS } from '../config';
import { isHeicInputMime, normalizeHeicIfNeeded } from './heic-helpers.js';
import {
  ERR_IMAGE_FORMAT_UNRECOGNIZED,
  ERR_UNSUPPORTED_IMAGE_FORMAT,
} from './image-errors.js';

export { ERR_IMAGE_FORMAT_UNRECOGNIZED, ERR_UNSUPPORTED_IMAGE_FORMAT } from './image-errors.js';
export { normalizeHeicIfNeeded } from './heic-helpers.js';

// --- MIME / filename --------------------------------------------------------

/**
 * True if the type is empty/unknown (caller may still try to decode), listed in config,
 * or HEIC/HEIF (converted before canvas use).
 */
export function isSupportedImageMime(mime) {
  if (!mime || typeof mime !== 'string') {
    return true;
  }
  const t = mime.trim().toLowerCase();
  if (!t) {
    return true;
  }
  if (isHeicInputMime(t)) {
    return true;
  }
  return SUPPORTED_IMAGE_FORMATS.some(m => m.toLowerCase() === t);
}

/** MIME for canvas.toBlob: canonical supported type, otherwise image/jpeg. */
export function resolveOutputImageMime(mime) {
  if (!mime || typeof mime !== 'string') {
    return 'image/jpeg';
  }
  const found = SUPPORTED_IMAGE_FORMATS.find(
    m => m.toLowerCase() === mime.trim().toLowerCase()
  );
  return found || 'image/jpeg';
}

// Get an URL from a theCard photo: either create a data: URL or return reference URL.
export function makeImageUrl(photo) {
  if (photo && typeof photo == 'object') {
    if (photo.ref) {
      return photo.ref;
    }
    if (photo.data && photo.type) {
      const mime = photo.type.startsWith('image/') ? photo.type : ('image/' + photo.type);
      return 'data:' + mime + ';base64,' + photo.data;
    }
  }
  return null;
}

// Calculate linear dimensions for scaling image down to fit under a certain size.
// Returns an object which contains destination sizes, source sizes, and offsets
// into source (when making square images).
export function fitImageSize(width, height, maxWidth, maxHeight, forceSquare) {
  // Sanitize input
  width = width | 0;
  height = height | 0;
  maxWidth = maxWidth | 0;
  maxHeight = maxHeight | 0;

  if (width <= 0 || height <= 0 || maxWidth <= 0 || maxHeight <= 0) {
    return null;
  }

  if (forceSquare) {
    maxWidth = maxHeight = Math.min(maxWidth, maxHeight);
  }

  const scale = Math.min(
    Math.min(width, maxWidth) / width,
    Math.min(height, maxHeight) / height
  );

  const size = {
    dstWidth: (width * scale) | 0,
    dstHeight: (height * scale) | 0,
  };

  if (forceSquare) {
    size.dstWidth = size.dstHeight = Math.min(size.dstWidth, size.dstHeight);
    size.srcWidth = size.srcHeight = Math.min(width, height);
    size.xoffset = ((width - size.srcWidth) / 2) | 0;
    size.yoffset = ((height - size.srcWidth) / 2) | 0;
  } else {
    size.xoffset = size.yoffset = 0;
    size.srcWidth = width;
    size.srcHeight = height;
  }
  return size;
}

// Ensure file's extension matches mime content type
export function fileNameForMime(fname, mime) {
  const idx = SUPPORTED_IMAGE_FORMATS.indexOf(mime);
  if (idx < 0 || !fname) {
    return fname;
  }
  const ext = MIME_EXTENSIONS[idx];
  const at = fname.lastIndexOf('.');
  if (at >= 0) {
    fname = fname.substring(0, at);
  }
  return fname + '.' + ext;
}

// --- Canvas / Image loading (internal) --------------------------------------

const SHRINK_FACTOR = 0.70710678118; // 1/sqrt(2) — each step halves pixel count ~

function canvasToBlob(canvas, mime) {
  return new Promise(resolve => canvas.toBlob(resolve, mime));
}

/**
 * Loads an image from an object URL. Revokes the URL after load (or on error if opts.revokeSrcOnError).
 */
function loadImageFromUrl(src, { revokeSrcOnError = true } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      if (revokeSrcOnError) {
        URL.revokeObjectURL(src);
      }
      reject(new Error(ERR_IMAGE_FORMAT_UNRECOGNIZED));
    };
    img.src = src;
  });
}

async function shrinkScaledOutputToMaxSize(img, dim, mime, maxSize) {
  const canvas = document.createElement('canvas');
  const redraw = () => {
    canvas.width = dim.dstWidth;
    canvas.height = dim.dstHeight;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      img,
      dim.xoffset,
      dim.yoffset,
      dim.srcWidth,
      dim.srcHeight,
      0,
      0,
      dim.dstWidth,
      dim.dstHeight
    );
  };

  redraw();
  let outBlob = await canvasToBlob(canvas, mime);
  if (!outBlob) {
    throw new Error(ERR_UNSUPPORTED_IMAGE_FORMAT);
  }

  while (maxSize > 0 && outBlob.size > maxSize) {
    dim.dstWidth = (dim.dstWidth * SHRINK_FACTOR) | 0;
    dim.dstHeight = (dim.dstHeight * SHRINK_FACTOR) | 0;
    redraw();
    outBlob = await canvasToBlob(canvas, mime);
  }

  return outBlob;
}

async function runImageScaled(blob, name, maxWidth, maxHeight, maxSize, forceSquare) {
  if (blob.type && !isSupportedImageMime(blob.type)) {
    throw new Error(ERR_UNSUPPORTED_IMAGE_FORMAT);
  }

  const objectUrl = URL.createObjectURL(blob);
  const img = await loadImageFromUrl(objectUrl, { revokeSrcOnError: true });

  const dim = fitImageSize(img.width, img.height, maxWidth, maxHeight, forceSquare);
  if (!dim) {
    throw new Error('Invalid image');
  }

  const mime = resolveOutputImageMime(blob.type);
  const outBlob = await shrinkScaledOutputToMaxSize(img, dim, mime, maxSize);

  return {
    mime,
    blob: outBlob,
    width: dim.dstWidth,
    height: dim.dstHeight,
    name: fileNameForMime(name, mime),
  };
}

// Scale uploaded image to fit under certain dimensions and byte size, optionally square.
export function imageScaled(fileOrBlob, maxWidth, maxHeight, maxSize, forceSquare) {
  return normalizeHeicIfNeeded(fileOrBlob).then(({ blob, name }) =>
    runImageScaled(blob, name, maxWidth, maxHeight, maxSize, forceSquare)
  );
}

async function runImageCrop(rasterBlob, converted, fallbackObjUrl, mime, left, top, cropW, cropH, scale) {
  const imageUrl = converted ? URL.createObjectURL(rasterBlob) : fallbackObjUrl;
  const effectiveMime = converted ? (rasterBlob.type || 'image/jpeg') : mime;

  if (effectiveMime && !isSupportedImageMime(effectiveMime)) {
    if (converted) {
      URL.revokeObjectURL(imageUrl);
    }
    throw new Error(ERR_UNSUPPORTED_IMAGE_FORMAT);
  }

  const img = await loadImageFromUrl(imageUrl, { revokeSrcOnError: converted });

  const canvas = document.createElement('canvas');
  canvas.width = cropW * scale;
  canvas.height = cropH * scale;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.drawImage(img, left, top, cropW, cropH, 0, 0, canvas.width, canvas.height);

  const outMime = resolveOutputImageMime(effectiveMime);

  return new Promise((resolve, reject) => {
    canvas.toBlob(outBlob => {
      if (outBlob) {
        resolve({ mime: outMime, blob: outBlob, width: cropW, height: cropH });
      } else {
        reject(new Error(ERR_UNSUPPORTED_IMAGE_FORMAT));
      }
    }, outMime);
  });
}

// Crop rectangle from image (coordinates in unscaled pixels), then scale.
export function imageCrop(mime, objURL, left, top, width, height, scale) {
  return fetch(objURL)
    .then(r => r.blob())
    .then(blob =>
      normalizeHeicIfNeeded(new File([blob], 'image', { type: mime || blob.type || '' }))
    )
    .then(({ blob: rasterBlob, converted }) =>
      runImageCrop(rasterBlob, converted, objURL, mime, left, top, width, height, scale)
    );
}

// --- Base64 & clipboard ------------------------------------------------------

/**
 * Reads a Blob or File as a data URL and returns mime + base64 payload.
 * Includes `name` when `source` is a File (has a string name).
 */
function readAsDataURLParts(source) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const bits = reader.result.split(',')[1];
      const out = { mime: source.type, bits };
      if (typeof source.name === 'string') {
        out.name = source.name;
      }
      resolve(out);
    };
    reader.readAsDataURL(source);
  });
}

export function fileToBase64(file) {
  return readAsDataURLParts(file);
}

export function blobToBase64(blob) {
  return readAsDataURLParts(blob);
}

// File pasted from the clipboard. It's either an inline image or a file attachment.
export function filePasted(event, onImageSuccess, onAttachmentSuccess, onError) {
  const items = (event.clipboardData || event.originalEvent.clipboardData || {}).items;
  if (!items || !items.length) {
    return false;
  }

  for (let i in items) {
    const item = items[i];
    if (item.kind === 'file') {
      const file = item.getAsFile();
      if (!file) {
        console.error("Failed to get file object from pasted file item", item.kind, item.type);
        onError("Failed to get file object from pasted file item");
        continue;
      }
      if (file.type && file.type.split('/')[0] == 'image') {
        onImageSuccess(file);
      } else {
        onAttachmentSuccess(file);
      }
      return true;
    }
  }
  return false;
}

export function getMimeType(header) {
  const mime = /^data:(image\/[-+a-z0-9.]+);base64/.exec(header);
  return mime && mime.length > 1 ? mime[1] : null;
}

// Given length of a binary object in bytes, calculate the length after
// base64 encoding.
export function base64EncodedLen(n) {
  return Math.floor((n + 2) / 3) * 4;
}

// Given length of a base64-encoded object, calculate decoded size of the
// pbject in bytes.
export function base64DecodedLen(n) {
  return Math.floor(n / 4) * 3;
}

// Re-encode string to standard base64 encoding with padding.
// The string may be base64-URL encoded without the padding.
export function base64ReEncode(str) {
  if (str) {
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    try {
      str = btoa(atob(str));
    } catch(err) {
      console.error("Failed to base64 re-encode string.", err);
      str = null;
    }
  }
  return str;
}

// Convert a base64 encoded string with the provided mime type into a Blob.
export function base64ToBlob(str, mime) {
  if (!str) {
    return null;
  }

  try {
    // Make blob.
    const bin = atob(str);
    const length = bin.length;
    const buf = new ArrayBuffer(length);
    const arr = new Uint8Array(buf);
    for (let i = 0; i < length; i++) {
      arr[i] = bin.charCodeAt(i);
    }

    return new Blob([buf], { type: mime });
  } catch(err) {
    console.error("Failed to convert base64 to blob: ", err);
  }

  return null;
}

export function intArrayToBase64(arr) {
  if (!Array.isArray(arr)) {
    return null;
  }
  try {
    let bin = '';
    new Uint8Array(arr).forEach(b => (bin += String.fromCharCode(b)));
    return window.btoa(bin);
  } catch (err) {}
  return null;
}

export function base64ToIntArray(b64) {
  const arr = [];
  try {
    const bin = window.atob(b64);
    [...bin].forEach(c => {
      arr.push(c.charCodeAt(0));
    });
    return arr;
  } catch (err) {}
  return null;
}
